import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.SsoClients.Secret;

type Props = {
  clientId: string;
  secret: string;
  onClose: () => void;
};

/** The one and only time the secret is readable; the server keeps a hash. */
export const ClientSecretDialog = ({ clientId, secret, onClose }: Props) => {
  const { t } = useResource();

  return (
    // Only the button closes it: an accidental Escape or backdrop click would lose the secret for good.
    <Dialog
      open
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t(RESOURCES.Title, { clientId })}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Alert severity="warning">{t(RESOURCES.Warning)}</Alert>
          <Typography variant="caption" color="text.secondary">
            {t(RESOURCES.Label)}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              component="code"
              fontFamily="monospace"
              sx={{ wordBreak: "break-all" }}
            >
              {secret}
            </Typography>
            <CopyToClipboard
              text={secret}
              accessibilityLabel={t(RESOURCES.Copy)}
              hoverMessage={t(RESOURCES.Copy)}
              successMessage={t(RESOURCES.Copied)}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t(RESOURCES.Close)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
